import {
  ActionIcon,
  Badge,
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBelegungMonate,
  useDeleteGruppe,
  useGruppen,
  useUpdateGruppe,
  useUpsertBelegungMonat,
} from '@/api/useBelegung';
import { useIntegrationKinder } from '@/api/useIntegrationKinder';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import type { BelegungMonatRow, GruppePlatz, GruppeRow, UUID } from '@/api/types';
import { GruppeEditDialog } from './GruppeEditDialog';
import { IntegrationKinderTabelle } from './IntegrationKinderFeld';
import {
  BETREUUNGSMODELL_VALUES,
  describeApiError,
} from './utils';

interface BelegungTabProps {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/**
 * Die anzuzeigenden Platz-Zeilen einer Gruppe.
 *
 * Hat die Gruppe noch keine, steht trotzdem eine da — mit dem ersten
 * Betreuungsmodell und einer 0. Das Eingabefeld soll vorhanden sein, nicht
 * erst nach einem Klick auf "Betreuungsmodell hinzufuegen" erscheinen.
 *
 * Gespeichert wird diese Zeile dadurch nicht: Erst wenn jemand eine Zahl
 * eintraegt oder das Modell wechselt, geht sie zum Server. Eine angezeigte
 * Zeile ist noch keine Angabe ueber die Betriebserlaubnis.
 */
function platzZeilen(g: GruppeRow): GruppePlatz[] {
  if (g.plaetze.length > 0) return g.plaetze;
  return [{ betreuungsmodell: BETREUUNGSMODELL_VALUES[0], anzahl: 0 }];
}

/** § 32 HKJGB: Massgeblich sind die Verhaeltnisse am 1. Maerz. */
const STICHTAG_MAERZ = 3;


function findCell(
  rows: BelegungMonatRow[],
  gruppeId: UUID,
  monat: number,
  modell: string,
): BelegungMonatRow | undefined {
  return rows.find(
    (r) => r.gruppe_id === gruppeId && r.monat === monat && r.betreuungsmodell === modell,
  );
}

export function BelegungTab({ planId, disabled, onMutated }: BelegungTabProps) {
  const { t } = useTranslation(['belegung', 'common', 'errors']);

  const gruppen = useGruppen(planId);
  const monate = useBelegungMonate(planId);
  const upsert = useUpsertBelegungMonat(planId);
  // Integrationszeile der Matrix zeigt nur noch die aus der Kinderliste
  // abgeleitete Anzahl (Task 8) — die Kinderliste ist die einzige Pflegestelle.
  const integrationKinder = useIntegrationKinder(planId);

  const deleteGruppe = useDeleteGruppe(planId);
  const updateGruppe = useUpdateGruppe(planId);

  /**
   * Schreibt die Platz-Zeilen einer Gruppe.
   *
   * Immer die vollstaendige Liste: Das Backend ersetzt sie, damit sich eine
   * Zeile ueberhaupt entfernen laesst. Zeilen ohne Betreuungsmodell bleiben
   * hier — sie sind angefangene Eingaben und wuerden serverseitig ohnehin
   * verworfen; wer sie wegschickt, verliert die Zeile unter dem Cursor.
   */
  const persistPlaetze = async (gruppe: GruppeRow, zeilen: GruppePlatz[]) => {
    try {
      await updateGruppe.mutateAsync({
        id: gruppe.id,
        patch: { plaetze: zeilen.filter((z) => z.betreuungsmodell !== '') },
      });
      onMutated();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeApiError(err) || t('errors:generic'),
      });
    }
  };

  /**
   * Monat, auf den sich die Spalte "I-Kinder" bezieht.

   * Die Spalte daneben ("Kinder laut Betriebserlaubnis") kennt keinen
   * Stichtag: Dort steht, was der Traeger selbst eingetragen hat, und das
   * gilt fuer das ganze Jahr.
   *
   * Vorbelegt mit dem 1. Maerz: Fuer die §32-Zuwendungen sind die
   * Verhaeltnisse an diesem Stichtag massgeblich, und genau diese Zahlen
   * prueft ein Traeger als erstes nach. Umstellbar, weil sich dieselbe
   * Frage auch fuer jeden anderen Monat stellt — etwa im September, wenn
   * die neuen Kinder da sind.
   *
   * Bewusst nur Ansichtszustand, nicht am Plan gespeichert: Es aendert
   * nichts an der Rechnung, und beim naechsten Aufruf soll wieder der
   * gesetzliche Stichtag dastehen.
   */
  const [stichtag, setStichtag] = useState<number>(STICHTAG_MAERZ);

  const [editGruppe, setEditGruppe] = useState<GruppeRow | null>(null);
  const [createGruppeOpen, setCreateGruppeOpen] = useState(false);
  const [deleteGruppeTarget, setDeleteGruppeTarget] = useState<GruppeRow | null>(null);

  const monateData = React.useMemo(() => monate.data ?? [], [monate.data]);

  const persistKinder = async (
    gruppeId: UUID,
    monat: number,
    modell: string,
    value: number,
  ) => {
    if (Number.isNaN(value) || value < 0) return;
    const existing = findCell(monateData, gruppeId, monat, modell);
    try {
      await upsert.mutateAsync({
        gruppe_id: gruppeId,
        monat,
        betreuungsmodell: modell,
        anzahl_kinder: value,
        integration_anzahl: existing?.integration_anzahl ?? 0,
      });
      // Ausfüllhilfe: Januar-Eingabe (>0) wird in Feb..Dez kopiert, sofern
      // diese Zellen noch leer (anzahl_kinder === 0) sind. So bleiben
      // explizit gesetzte Monatswerte unangetastet.
      if (monat === 1 && value > 0) {
        const targets: number[] = [];
        for (let m = 2; m <= 12; m += 1) {
          const c = findCell(monateData, gruppeId, m, modell);
          if (!c || (c.anzahl_kinder ?? 0) === 0) targets.push(m);
        }
        await Promise.all(
          targets.map((m) => {
            const c = findCell(monateData, gruppeId, m, modell);
            return upsert.mutateAsync({
              gruppe_id: gruppeId,
              monat: m,
              betreuungsmodell: modell,
              anzahl_kinder: value,
              integration_anzahl: c?.integration_anzahl ?? 0,
            });
          }),
        );
      }
      onMutated();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeApiError(err) || t('errors:generic'),
      });
    }
  };

  return (
    <Stack>
      {/* ─── Themenblock Gruppen ─── */}
      <Paper
        withBorder
        p="md"
        bg="var(--mantine-color-gray-0)"
        style={{ borderColor: 'var(--mantine-color-gray-3)' }}
      >
        <Text
          size="xs"
          tt="uppercase"
          fw={700}
          c="dimmed"
          mb="sm"
          style={{ letterSpacing: 1 }}
        >
          {t('belegung:section.gruppen')}
        </Text>
      <Paper withBorder p="md">
        <Group justify="space-between" mb="sm" wrap="nowrap" align="flex-end">
          <Title order={5}>{t('belegung:section.gruppen')}</Title>
          <Group gap="sm" wrap="nowrap" align="flex-end">
            <Select
              label={t('belegung:gruppe.stichtag')}
              data={MONTHS.map((m) => ({
                value: String(m),
                label: t(`common:month.long.${m}`),
              }))}
              value={String(stichtag)}
              onChange={(v) => setStichtag(v ? Number(v) : STICHTAG_MAERZ)}
              allowDeselect={false}
              size="xs"
              w={190}
              aria-label={t('belegung:gruppe.stichtag')}
            />
            {!disabled ? (
              <Button
                leftSection={<IconPlus size={14} />}
                size="xs"
                onClick={() => setCreateGruppeOpen(true)}
              >
                {t('belegung:gruppe.add')}
              </Button>
            ) : null}
          </Group>
        </Group>
        {gruppen.isLoading ? <LoadingState height={120} /> : null}
        {gruppen.isError ? <ErrorState error={gruppen.error} /> : null}
        {gruppen.data && gruppen.data.length === 0 ? (
          <Text c="dimmed">{t('belegung:matrix.empty')}</Text>
        ) : null}
        {gruppen.data && gruppen.data.length > 0 ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('belegung:gruppe.name')}</Table.Th>
                <Table.Th>{t('belegung:form.field.aktiv')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  <Tooltip label={t('belegung:gruppe.kinderanzahlHint')}>
                    <span>{t('belegung:gruppe.kinderanzahl')}</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  <Tooltip label={t('belegung:gruppe.iKinderHint')}>
                    <span>{t('belegung:gruppe.iKinder')}</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th aria-label={t('common:actions.edit')} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {gruppen.data.flatMap((g) => [
                <Table.Tr key={g.id}>
                  <Table.Td>{g.name}</Table.Td>
                  <Table.Td>
                    <Badge color={g.aktiv ? 'green' : 'gray'} variant="light">
                      {g.aktiv ? t('common:yes') : t('common:no')}
                    </Badge>
                  </Table.Td>
                  <Table.Td
                    style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                    data-testid={`gruppe-kinder-${g.id}`}
                  >
                    {/* Die Summe der Platz-Zeilen darunter. Bewusst NICHT
                        die Belegung aus der Matrix: Die steht in der Matrix
                        selbst, Monat fuer Monat, und haette hier nur einen
                        willkuerlich gewaehlten Stichtag abgebildet. */}
                    {g.plaetze.reduce((summe, p) => summe + p.anzahl, 0)}
                  </Table.Td>
                  <Table.Td
                    style={{ textAlign: 'right' }}
                    data-testid={`gruppe-ikinder-${g.id}`}
                  >
                    {
                      (integrationKinder.data ?? []).filter(
                        (k) =>
                          k.gruppe_id === g.id &&
                          k.aktiv_ab_monat <= stichtag &&
                          stichtag <= k.aktiv_bis_monat,
                      ).length
                    }
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      {!disabled ? (
                        <>
                          <Tooltip label={t('common:actions.edit')}>
                            <ActionIcon
                              variant="subtle"
                              onClick={() => setEditGruppe(g)}
                              aria-label={t('common:actions.edit')}
                            >
                              <IconEdit size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common:actions.delete')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => setDeleteGruppeTarget(g)}
                              aria-label={t('common:actions.delete')}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>,
                // --- Plaetze laut Betriebserlaubnis, je Betreuungsmodell ---
                //
                // Als Unterzeilen der Gruppe, mit dem Eingabefeld unter der
                // Spaltenueberschrift "Kinder laut Betriebserlaubnis".
                //
                // Eine Gruppe ohne hinterlegte Plaetze zeigt trotzdem eine
                // Zeile: Das Eingabefeld soll dastehen, nicht erst nach
                // einem Klick auf "hinzufuegen" erscheinen. Gespeichert
                // wird sie erst, wenn etwas drinsteht — eine angezeigte
                // Zeile ist noch keine Angabe.
                ...platzZeilen(g).map((zeile, i) => (
                  <Table.Tr key={`${g.id}-platz-${i}`}>
                    {/* Die beiden Eingabefelder stehen so weit rechts, wie
                        das Spaltenraster es zulaesst: die Zahl genau unter
                        "Kinder laut Betriebserlaubnis", das Modell direkt
                        davor. Beide gehoeren zu dieser Spalte, nur passen
                        sie nicht in eine Zelle. */}
                    <Table.Td />
                    <Table.Td>
                      <Select
                        data={BETREUUNGSMODELL_VALUES.map((m) => ({
                          value: m,
                          label: t(`belegung:betreuungsmodell.option.${m}`),
                        }))}
                        value={zeile.betreuungsmodell || null}
                        onChange={(v) => {
                          // Ein schon vergebenes Modell wuerde den
                          // Unique-Index reissen — die Auswahl bleibt dann
                          // einfach stehen.
                          const zeilen = platzZeilen(g);
                          if (v && zeilen.some((z, j) => j !== i && z.betreuungsmodell === v)) {
                            notifications.show({
                              color: 'yellow',
                              message: t('belegung:form.field.plaetzeDoppelt'),
                            });
                            return;
                          }
                          void persistPlaetze(
                            g,
                            zeilen.map((z, j) =>
                              j === i ? { ...z, betreuungsmodell: v ?? '' } : z,
                            ),
                          );
                        }}
                        disabled={disabled}
                        size="xs"
                        w={200}
                        searchable
                        aria-label={`${g.name} ${t('belegung:form.field.betreuungsmodell')}`}
                        ml="auto"
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        min={0}
                        max={999}
                        allowDecimal={false}
                        size="xs"
                        w={90}
                        defaultValue={zeile.anzahl}
                        key={`${zeile.betreuungsmodell}-${zeile.anzahl}`}
                        onBlur={(e) => {
                          const wert = Number(e.currentTarget.value);
                          if (Number.isNaN(wert) || wert === zeile.anzahl) return;
                          void persistPlaetze(
                            g,
                            platzZeilen(g).map((z, j) =>
                              j === i ? { ...z, anzahl: Math.max(0, wert) } : z,
                            ),
                          );
                        }}
                        disabled={disabled}
                        styles={{ input: { textAlign: 'right' } }}
                        aria-label={`${g.name} ${t('belegung:form.field.plaetzeAnzahl')} ${
                          zeile.betreuungsmodell
                        }`}
                        ml="auto"
                      />
                    </Table.Td>
                    <Table.Td />
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        {/* Eine nur angezeigte Zeile gibt es noch nicht —
                            da ist auch nichts zu loeschen. */}
                        {!disabled && g.plaetze.length > 0 ? (
                          <Tooltip label={t('common:actions.delete')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() =>
                                void persistPlaetze(
                                  g,
                                  g.plaetze.filter((_, j) => j !== i),
                                )
                              }
                              aria-label={`${t('common:actions.delete')} ${zeile.betreuungsmodell}`}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )),
                !disabled ? (
                  <Table.Tr key={`${g.id}-platz-add`}>
                    <Table.Td />
                    <Table.Td colSpan={2} style={{ textAlign: 'right' }}>
                      {(() => {
                        // Je Gruppe und Modell hoechstens eine Zeile — zwei
                        // waeren zwei Wahrheiten ueber dieselbe Platzzahl,
                        // und die Datenbank laesst sie ohnehin nicht zu. Die
                        // neue Zeile bekommt deshalb das erste noch freie
                        // Modell; sind alle vergeben, gibt es nichts mehr
                        // hinzuzufuegen.
                        const belegt = new Set(platzZeilen(g).map((z) => z.betreuungsmodell));
                        const frei = BETREUUNGSMODELL_VALUES.find((m) => !belegt.has(m));
                        return (
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            leftSection={<IconPlus size={12} />}
                            disabled={frei === undefined}
                            onClick={() =>
                              frei === undefined
                                ? undefined
                                : void persistPlaetze(g, [
                                    ...platzZeilen(g),
                                    { betreuungsmodell: frei, anzahl: 0 },
                                  ])
                            }
                          >
                            {t('belegung:form.field.plaetzeAdd')}
                          </Button>
                        );
                      })()}
                    </Table.Td>
                    <Table.Td colSpan={2} />
                  </Table.Tr>
                ) : null,
              ])}
            </Table.Tbody>
          </Table>
        ) : null}
      </Paper>
      </Paper>

      {/* ─── Themenblock Inklusion ─── */}
      {gruppen.data && gruppen.data.length > 0 ? (
        <Paper
          withBorder
          p="md"
          bg="var(--mantine-color-gray-0)"
          style={{ borderColor: 'var(--mantine-color-gray-3)' }}
        >
          <Text
            size="xs"
            tt="uppercase"
            fw={700}
            c="dimmed"
            mb="sm"
            style={{ letterSpacing: 1 }}
          >
            {t('belegung:section.inklusion.gruppe')}
          </Text>
          {/* Die Kinder werden hier gepflegt — bei der Belegung, zu der sie
              gehören. Was sich daraus für den Personalbedarf ergibt, steht
              im Reiter "Personalbedarf" beim Mindestpersonalbedarf. */}
          <div data-testid="inklusion-block">
            <IntegrationKinderTabelle
              planId={planId}
              gruppen={gruppen.data ?? []}
              disabled={disabled}
            />
          </div>
        </Paper>
      ) : null}

      {/* ─── Belegungsmatrix-Section ─── */}
      {gruppen.data && gruppen.data.length > 0 ? (
        <Paper withBorder p="md">
          <Title order={4} mb="sm">
            {t('belegung:section.matrix')}
          </Title>
          <Table.ScrollContainer minWidth={1400}>
            <Table withTableBorder withColumnBorders striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 160 }}>
                    {t('belegung:matrix.gruppe.heading')}
                  </Table.Th>
                  <Table.Th style={{ minWidth: 140 }}>
                    {t('belegung:matrix.col.modell')}
                  </Table.Th>
                  {MONTHS.map((m) => (
                    <Table.Th key={m} style={{ textAlign: 'right', minWidth: 70 }}>
                      {t(`common:month.short.${m}`)}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {gruppen.data.flatMap((g) => {
                  const rows: React.ReactNode[] = [];
                  // Modell-Zeilen (anzahl_kinder)
                  BETREUUNGSMODELL_VALUES.forEach((modell, idx) => {
                    rows.push(
                      <Table.Tr key={`${g.id}-${modell}`}>
                        {idx === 0 ? (
                          <Table.Td
                            rowSpan={BETREUUNGSMODELL_VALUES.length + 1}
                            style={{ verticalAlign: 'top', fontWeight: 600 }}
                          >
                            {g.name}
                          </Table.Td>
                        ) : null}
                        <Table.Td>
                          <Text size="sm">
                            {t(`belegung:betreuungsmodell.option.${modell}`)}
                          </Text>
                        </Table.Td>
                        {MONTHS.map((m) => {
                          const cell = findCell(monateData, g.id, m, modell);
                          return (
                            <Table.Td key={`${g.id}-${modell}-${m}`}>
                              <NumberInput
                                size="xs"
                                hideControls
                                value={cell && cell.anzahl_kinder > 0 ? cell.anzahl_kinder : ''}
                                onBlur={(e) =>
                                  void persistKinder(
                                    g.id,
                                    m,
                                    modell,
                                    e.currentTarget.value === ''
                                      ? 0
                                      : Number(e.currentTarget.value),
                                  )
                                }
                                disabled={disabled}
                                styles={{ input: { textAlign: 'right' } }}
                                aria-label={`${g.name} ${t(
                                  `belegung:betreuungsmodell.option.${modell}`,
                                )} ${t(`common:month.short.${m}`)}`}
                              />
                            </Table.Td>
                          );
                        })}
                      </Table.Tr>,
                    );
                  });
                  // Integrations-Zeile: abgeleitet aus der Kinderliste,
                  // nicht mehr editierbar (eine Pflegestelle, keine zwei).
                  rows.push(
                    <Table.Tr key={`${g.id}-integration`}>
                      <Table.Td>
                        <Text size="sm" c="dimmed" fs="italic">
                          {t('belegung:matrix.row.integration')}
                        </Text>
                      </Table.Td>
                      {MONTHS.map((m) => {
                        const anzahl = (integrationKinder.data ?? []).filter(
                          (k) =>
                            k.gruppe_id === g.id &&
                            k.aktiv_ab_monat <= m &&
                            m <= k.aktiv_bis_monat,
                        ).length;
                        return (
                          <Table.Td key={`${g.id}-integ-${m}`}>
                            <Text size="xs" ta="right" c={anzahl > 0 ? undefined : 'dimmed'}>
                              {anzahl > 0 ? anzahl : '–'}
                            </Text>
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>,
                  );
                  return rows;
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      ) : (
        <EmptyState description={t('belegung:matrix.empty')} />
      )}

      <GruppeEditDialog
        opened={createGruppeOpen}
        planId={planId}
        gruppe={null}
        onClose={() => setCreateGruppeOpen(false)}
      />
      <GruppeEditDialog
        opened={editGruppe !== null}
        planId={planId}
        gruppe={editGruppe}
        onClose={() => setEditGruppe(null)}
      />

      {deleteGruppeTarget ? (
        <ConfirmDialog
          opened
          title={t('belegung:gruppe.remove')}
          message={t('belegung:gruppe.removeConfirm', { name: deleteGruppeTarget.name })}
          confirmLabel={t('common:actions.delete')}
          danger
          onCancel={() => setDeleteGruppeTarget(null)}
          onConfirm={async () => {
            const target = deleteGruppeTarget;
            setDeleteGruppeTarget(null);
            try {
              await deleteGruppe.mutateAsync(target.id);
              notifications.show({ color: 'teal', message: t('belegung:info.deleted') });
              onMutated();
            } catch (err) {
              notifications.show({
                color: 'red',
                title: t('errors:generic'),
                message: describeApiError(err) || t('errors:generic'),
              });
            }
          }}
        />
      ) : null}
    </Stack>
  );
}
